import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../config/axios";
import PageWrapper from "../components/common/PageWrapper";
import Header from "../components/common/Header";
import BodyWrapper from "../components/common/BodyWrapper";
import Footer from "../components/common/Footer";
import ProductCard from "../components/shopPartials/ProductCard";
import Filters from "../components/shopPartials/Filters";
import { useGeneral } from "../components/contexts/GeneralContext";
import FullScreenLoading from "../components/common/loading/FullScreenLoading";
import NoItemsFound from "../components/common/NoItemsFound";
import ApiErrorMessage from "../components/common/ApiErrorMessage";
import SearchBar from "../components/common/SearchBar";
import SelectBox from "../components/common/SelectBox";
import { motion } from "framer-motion";
import "../components/shopPartials/shop.css";
import { formatString } from "../components/utils/commonHelper";
import GeneralButton from "../components/common/GeneralButton";
import { useCart } from "../components/contexts/CartContext";

const usePageContent = (page, filters) => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({
    items: [],
    pagination: {},
    categories: [],
    materials: [],
    priceRange: { minValue: 0, maxValue: null },
  });
  const { cart } = useCart();
  const [error, setError] = useState("");
  const { discountCoupons } = useGeneral();
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
  const cartId = localStorage.getItem("cartId") || cart?._id || null;

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    (async () => {
      try {
        setLoading(true);

        // ✅ Prepare params for filtering and pagination
        const params = new URLSearchParams();

        params.set("page", page);
        if (loggedInUser?._id) params.set("customerId", loggedInUser._id);
        if (filters.search) params.set("q", filters.search);
        if (filters.sort) params.set("sort", filters.sort);
        // Replace existing params.append logic with this
        if (filters.categories && filters.categories.length > 0) {
          const categoriesArray = Array.isArray(filters.categories)
            ? filters.categories
            : [filters.categories];
          categoriesArray.forEach((cat) => {
            params.append("categories[]", cat.trim());
          });
        }

        if (filters.materials && filters.materials.length > 0) {
          const materialsArray = Array.isArray(filters.materials)
            ? filters.materials
            : [filters.materials];
          materialsArray.forEach((mat) => {
            params.append("materials[]", mat.trim());
          });
        }

        if (filters.priceRange?.minValue)
          params.set("minPrice", filters.priceRange.minValue);
        if (filters.priceRange?.maxValue)
          params.set("maxPrice", filters.priceRange.maxValue);
        if (filters.discounted === "1") params.set("discounted", "1");
        if (filters.inStock === "1") params.set("inStock", "1");

        // ✅ API call with proper query string
        const { data } = await axios.post(
          `/products?${params.toString()}`,
          { cartId: cart?.orderCart?._id },
          { signal: controller.signal }
        );

        if (active) {
          const min = Number(data?.priceRange?.minValue?.$numberDecimal ?? 0);
          const max = Number(data?.priceRange?.maxValue?.$numberDecimal ?? 0);
          setContent((prev) => ({
            items: page === 1 ? data.items : [...prev.items, ...data.items],
            pagination: data.pagination,
            categories: data.categories,
            materials: data.materials,
            priceRange: { minValue: min, maxValue: max },
          }));
        }
      } catch (err) {
        if (err.name !== "CanceledError") {
          console.error(err);
          if (active)
            setError("Failed to load products. Please try again later.");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [page, filters, cart]); // ✅ Include cartId

  return { content, loading, error, discountCoupons };
};

/* ---------------------- Scroll to Top ---------------------- */
const useScrollToTop = () => {
  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, []);
};

const ShopPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth < 768); // match Tailwind md
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  /* ✅ Parse filters from URL */
  const getFiltersFromURL = () => {
    const params = new URLSearchParams(location.search);
    return {
      search: params.get("search") || "",
      sort: params.get("sort") || "",
      categories: params.getAll("categories[]") || [],
      materials: params.getAll("materials[]") || [],
      priceRange:
        params.get("minPrice") || params.get("maxPrice")
          ? {
              minValue: Number(params.get("minPrice")) || 0,
              maxValue: Number(params.get("maxPrice")) || 0,
            }
          : {},
      discounted: params.get("discounted") || "0",
      inStock: params.get("inStock") || "0",
    };
  };

  const [filters, setFilters] = useState(getFiltersFromURL);
  const [inputValue, setInputValue] = useState(filters.search || "");
  const { content, loading, error, discountCoupons } = usePageContent(
    page,
    filters
  );
  const [showFilters, setShowFilters] = useState(false);

  const sortOptions = [
    { label: "Price: Low to High", value: "low-price" },
    { label: "Price: High to Low", value: "high-price" },
    { label: "Newest to Oldest Arrivals", value: "newest" },
    { label: "Oldest to Newest Arrivals", value: "oldest" },
  ];

  useScrollToTop();

  /* ✅ Sync filters → URL */
  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.search) params.set("search", filters.search);
    if (filters.sort) params.set("sort", filters.sort);
    // Replace existing categories/materials params.set logic with this
    if (filters.categories?.length) {
      filters.categories.forEach((cat) => {
        params.append("categories[]", cat);
      });
    }
    if (filters.materials?.length) {
      filters.materials.forEach((mat) => {
        params.append("materials[]", mat);
      });
    }
    if (filters.priceRange?.minValue)
      params.set("minPrice", filters.priceRange.minValue);
    if (filters.priceRange?.maxValue)
      params.set("maxPrice", filters.priceRange.maxValue);
    if (filters.discounted === "1") params.set("discounted", "1");
    if (filters.inStock === "1") params.set("inStock", "1");

    const query = params.toString();
    const newUrl = query ? `?${query}` : "";
    navigate(
      { pathname: location.pathname, search: newUrl },
      { replace: true }
    );
  }, [filters, navigate, location.pathname]);

  /* ✅ Reload filters if user navigates via back/forward */
  useEffect(() => {
    const newFilters = getFiltersFromURL();
    if (JSON.stringify(newFilters) !== JSON.stringify(filters)) {
      setFilters(newFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const toggleFilters = () => setShowFilters((prev) => !prev);

  const loadMore = useCallback(() => {
    if (page < content.pagination.pageCount) setPage((p) => p + 1);
  }, [page, content.pagination.pageCount]);

  const hasMore = useMemo(
    () => page < content.pagination.pageCount,
    [page, content.pagination.pageCount]
  );

  const handleSearch = (searchQuery) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, search: searchQuery }));
  };

  const handleClearSearch = () => {
    setInputValue("");
    setFilters((prev) => ({ ...prev, search: "" }));
  };

  const handleSelectChange = (val) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, sort: val }));
  };

  const handleApplyFilters = (appliedValues) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      categories: appliedValues.category || [],
      materials: appliedValues.materials || [],
      priceRange: appliedValues.priceRange
        ? {
            minValue: appliedValues.priceRange[0],
            maxValue: appliedValues.priceRange[1],
          }
        : {},
      discounted: appliedValues.discounted ?? "0",
      inStock: appliedValues.inStock ?? "0",
    }));
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  };

  return (
    <>
      {loading && page === 1 && <FullScreenLoading />}
      <PageWrapper loading={loading}>
        <Header code={discountCoupons} />
        <BodyWrapper loading={loading && page === 1}>
          {/* --- Controls (Search, Sort, Filters Toggle) --- */}
          {content?.pagination?.totalItems > 0 && (
            <div className="mb-4 flex flex-col items-start md:items-center justify-between gap-2 md:mt-2 mt-0">
              <div className="flex-1 flex flex-col md:flex-row items-center justify-end gap-2 w-full">
                <SearchBar
                  value={inputValue}
                  setValue={setInputValue}
                  onClear={handleClearSearch}
                  onSearch={handleSearch}
                />

                <SelectBox
                  value={filters.sort}
                  setValue={handleSelectChange}
                  options={sortOptions}
                  placeholder="Sort By"
                  className="2.5xl:max-w-[200px]"
                />

                <div
                  title={
                    showFilters
                      ? "Click to hide all filters"
                      : "Click to view all filters"
                  }
                  className={`filterBox w-full md:max-w-[150px] min-w-[150px] ${
                    showFilters ? "bg-secondary" : "bg-orange"
                  } text-white h-[41.6px] px-5 rounded-[10px] select-none cursor-pointer hover:shadow-xl flex items-center justify-center relative`}
                  onClick={toggleFilters}
                >
                  {showFilters && <div className="arrow-down z-[50]"></div>}
                  {showFilters ? "Hide all filters" : "View all filters"}
                </div>
              </div>

              {/* Applied filters summary, unchanged */}
              {(filters.search ||
                filters.categories?.length ||
                filters.materials?.length ||
                filters.priceRange?.maxValue > 0 ||
                filters.priceRange?.minValue > 0 ||
                filters.discounted === "1" ||
                filters.inStock === "1" ||
                filters.sort) && (
                <div className="flex flex-wrap items-center gap-2 mt-2 px-3 py-2 bg-[#D06F36]/10 rounded-lg w-full">
                  {/* Search Filter */}

                  {/* Show "Filters Applied" only if any filter except search/sort is applied */}
                  {(filters.categories?.length ||
                    filters.materials?.length ||
                    filters.priceRange?.maxValue > 0 ||
                    filters.priceRange?.minValue > 0 ||
                    filters.discounted === "1" ||
                    filters.inStock === "1") && (
                    <span className="text-sm font-medium text-gray-700 mr-0">
                      Filters Applied:
                    </span>
                  )}

                  {/* Categories */}
                  {filters.categories?.map((cat) => (
                    <div
                      key={cat}
                      className="flex items-center gap-1 text-orange px-2 py-1 rounded text-sm text-gray-800"
                    >
                      {formatString(cat)}
                      <button
                        onClick={() =>
                          setFilters({
                            ...filters,
                            categories: filters.categories.filter(
                              (c) => c !== cat
                            ),
                          })
                        }
                        title="Click to remove filter"
                        className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  ))}

                  {/* Materials */}
                  {filters.materials?.map((mat) => (
                    <div
                      key={mat}
                      className="flex items-center gap-1 text-orange px-2 py-1 rounded text-sm text-gray-800"
                    >
                      {formatString(mat)}
                      <button
                        onClick={() =>
                          setFilters({
                            ...filters,
                            materials: filters.materials.filter(
                              (m) => m !== mat
                            ),
                          })
                        }
                        title="Click to remove filter"
                        className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  ))}

                  {/* Price */}
                  {filters.priceRange?.maxValue > 0 && (
                    <div className="flex items-center gap-1 text-orange px-2 py-1 rounded text-sm text-gray-800">
                      Price: {filters.priceRange.minValue} -{" "}
                      {filters.priceRange.maxValue} AED
                      <button
                        onClick={() =>
                          setFilters({ ...filters, priceRange: null })
                        }
                        title="Click to remove filter"
                        className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {/* Discounted */}
                  {filters.discounted === "1" && (
                    <div className="flex items-center gap-1 text-orange px-2 py-1 rounded text-sm text-gray-800">
                      Discounted
                      <button
                        onClick={() =>
                          setFilters({ ...filters, discounted: "0" })
                        }
                        title="Click to remove filter"
                        className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {/* In Stock */}
                  {filters.inStock === "1" && (
                    <div className="flex items-center gap-1 text-orange px-2 py-1 rounded text-sm text-gray-800">
                      In Stock
                      <button
                        onClick={() => setFilters({ ...filters, inStock: "0" })}
                        title="Click to remove filter"
                        className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  {filters.search && (
                    <div className="flex items-center gap-1 text-orange me-2 py-1 rounded text-sm text-gray-700">
                      <span className="text-secondary"> Search: </span>
                      <span className="text-[#D06F36]">{filters.search}</span>
                      <button
                        onClick={() => setFilters({ ...filters, search: "" })}
                        title="Click to remove search"
                        className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  )}

                  {/* Sort Filter */}
                  {filters.sort && (
                    <div className="flex items-center gap-1 text-orange me-2 py-1 rounded text-sm text-gray-800">
                      <span className="text-secondary">Sort: </span>
                      {sortOptions.find((s) => s.value === filters.sort)?.label}
                      <button
                        onClick={() => {
                          setFilters({ ...filters, sort: "" });
                        }}
                        title="Click to remove sort "
                        className="ml-1 text-secondary hover:text-secondary/40 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  {/* Clear All */}
                  <button
                    onClick={() => {
                      handleClearSearch();

                      setFilters({
                        sort: "",
                        categories: [],
                        materials: [],
                        priceRange: null,
                        discounted: "0",
                        inStock: "0",
                      });
                    }}
                    className="ml-auto text-xs text-[#D06F36] font-medium hover:underline transition-all duration-200"
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>
          )}

          {/* --- Products Grid --- */}
          {!loading && error ? (
            <ApiErrorMessage
              message1="Failed to load products"
              message2="Please check your connection and try again."
            />
          ) : !loading && content?.items?.length === 0 ? (
            <NoItemsFound
              message1="No products found"
              message2={
                filters.search
                  ? "Please try a different search term."
                  : "Please try again later."
              }
            />
          ) : (
            <div
              className={
                !showFilters
                  ? "grid grid-cols-1"
                  : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[3fr_1fr] gap-5"
              }
            >
              {/* Product Grid */}
              <div
                className={
                  showFilters
                    ? "grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2.5xl:grid-cols-4 gap-2 md:gap-4 lg:gap-6 self-start order-2"
                    : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2.5xl:grid-cols-5 gap-2 md:gap-4 lg:gap-6 "
                }
              >
                {content?.items?.map((product) => (
                  <ProductCard key={product._id} product={product} />
                ))}

                {hasMore && (
                  <div className="col-span-full flex justify-center w-full mt-2 xl:mt-4">
                    <GeneralButton
                      text="Load More"
                      title="Click to load more items"
                      onClick={loadMore}
                      bgColor="#D06F36"
                    />
                  </div>
                )}

                {loading &&
                  page >= 1 &&
                  content?.pagination?.totalItems > 0 && (
                    <div className="col-span-full flex justify-center">
                      <div className="w-5 h-5 border-4 border-tertiary border border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
              </div>

              {showFilters && (
                <motion.div
                  initial={!isMobile ? { opacity: 0, x: 50 } : false}
                  animate={
                    !isMobile ? { opacity: 1, x: 0 } : { opacity: 1, x: 0 }
                  }
                  transition={!isMobile ? { duration: 0.3 } : { duration: 0 }}
                  className="bg-[#DEE2DD]/50 rounded-[15px] px-2 py-4 2xl:px-8 2xl:py-6 h-fit order-1 md:order-2"
                >
                  <Filters
                    content={content}
                    selectedFilters={filters}
                    onApplyFilters={handleApplyFilters}
                  />
                </motion.div>
              )}
            </div>
          )}
        </BodyWrapper>
        <Footer />
      </PageWrapper>
    </>
  );
};

export default ShopPage;
